"use client"

import { Component, type ReactNode } from "react"
import { ErrorState } from "@immi/ui/components/error-state"

import { analytics } from "@/lib/analytics/client"

interface ErrorBoundaryProps {
  children: ReactNode
  /** Fallback custom; se omitido, usa <ErrorState/>. */
  fallback?: ReactNode
  /** Label pra distinguir a origem nos logs/tracking. */
  label?: string
}

interface ErrorBoundaryState {
  hasError: boolean
}

/**
 * Rede de segurança pra falhas de render em componentes sob <Suspense>.
 * Sem isso, um throw durante SSR/hidratação vira React #419 (Suspense
 * boundary falhou) sem tratamento. Diferente do error.tsx de rota, este
 * cobre também componentes de layout.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error) {
    console.error(
      `[ErrorBoundary${this.props.label ? `:${this.props.label}` : ""}]`,
      error
    )
    analytics.captureException(error)
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? <ErrorState />
    }
    return this.props.children
  }
}
